"use client";

import { useCallback, useRef, useState } from "react";

function socialProofCount(): string {
  const raw = process.env.NEXT_PUBLIC_NEWSLETTER_SOCIAL_PROOF_COUNT;
  const n = raw != null && raw !== "" ? Number(String(raw).replace(/,/g, "")) : 4000;
  if (!Number.isFinite(n) || n < 1) return "4,000+";
  return `${new Intl.NumberFormat("en-ZA").format(Math.round(n))}+`;
}

type Props = {
  /** Stored on subscriber row for ESP automation (welcome sequence). */
  source: string;
  variant: "mid" | "exit";
};

/**
 * Blog → email list. Posts to /api/blog/weekly-ledger (same pipeline as Weekly Ledger).
 */
export function BlogNewsletterEmailCapture({ source, variant }: Props) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [msg, setMsg] = useState<string | null>(null);
  const hpRef = useRef<HTMLInputElement>(null);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setStatus("loading");
      setMsg(null);
      try {
        const res = await fetch("/api/blog/weekly-ledger", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            source,
            companyWebsite: hpRef.current?.value ?? "",
          }),
        });
        const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
        if (res.status === 429) {
          setStatus("err");
          setMsg(data.error || "Too many attempts. Please try again later.");
          return;
        }
        if (!res.ok || !data.ok) {
          setStatus("err");
          setMsg(data.error || "Could not subscribe right now.");
          return;
        }
        setStatus("ok");
        setMsg(variant === "exit" ? "You’re in — we’ll keep it useful." : "You’re on the list.");
        setEmail("");
      } catch {
        setStatus("err");
        setMsg("Network error — try again in a moment.");
      }
    },
    [email, source, variant],
  );

  if (variant === "exit") {
    const n = socialProofCount();
    return (
      <div className="ed-blog-cap ed-blog-cap--exit" data-variant="exit">
        <p className="ed-blog-cap__exit-line">
          Join {n} people tracking smarter →
        </p>
        <form className="ed-blog-cap__form ed-blog-cap__form--exit" onSubmit={(e) => void submit(e)} noValidate>
          <input
            ref={hpRef}
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="ed-blog-hp"
            defaultValue=""
          />
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            placeholder="you@email.com"
            className="ed-blog-cap__input ed-blog-cap__input--exit"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "loading"}
            aria-label="Email for tips"
          />
          <button type="submit" className="ed-blog-cap__btn ed-blog-cap__btn--exit" disabled={status === "loading"}>
            {status === "loading" ? "…" : "Join"}
          </button>
        </form>
        {msg ? <p className={`ed-blog-cap__msg${status === "err" ? " ed-blog-cap__msg--err" : ""}`}>{msg}</p> : null}
      </div>
    );
  }

  return (
    <aside className="ed-blog-cap ed-blog-cap--mid" aria-label="Email tips signup">
      <p className="ed-blog-cap__mid-copy">
        Get the weekly expiry &amp; inventory tip. No spam, one idea, always useful.
      </p>
      <form className="ed-blog-cap__form ed-blog-cap__form--mid" onSubmit={(e) => void submit(e)} noValidate>
        <input
          ref={hpRef}
          type="text"
          name="company_website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="ed-blog-hp"
          defaultValue=""
        />
        <input
          type="email"
          name="email"
          autoComplete="email"
          required
          placeholder="you@email.com"
          className="ed-blog-cap__input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "loading"}
          aria-label="Email address"
        />
        <button type="submit" className="ed-blog-cap__btn" disabled={status === "loading"}>
          {status === "loading" ? "Joining…" : "Get tips"}
        </button>
      </form>
      {msg ? <p className={`ed-blog-cap__msg${status === "err" ? " ed-blog-cap__msg--err" : ""}`}>{msg}</p> : null}
      <p className="ed-blog-cap__fine">Unsubscribe anytime. Welcome sequence starts automatically.</p>
    </aside>
  );
}
